"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PatentFormSchema = exports.FullPatentFormSchema = exports.PCTPatentFormSchema = exports.ProvisionalPatentFormSchema = exports.BasePatentFormSchema = void 0;
const patent_schema_1 = require("../core-model-schemas/patent-schema");
const patent_enums_1 = require("../enums/patent-enums");
const zod_1 = require("zod");
exports.BasePatentFormSchema = zod_1.z.object({
    /* REQUIRED FOR ANY AND ALL PATENT TYPES
     * name, caseId, status, patentType
     */
    name: zod_1.z
        .string()
        .min(3, 'Patent name must be at least 3 characters long')
        .max(200, 'Patent name must be under 200 characters'),
    caseId: zod_1.z.string().min(1, 'Case ID is required'),
    status: patent_enums_1.PatentStatusSchema,
    patentType: patent_enums_1.PatentTypeSchema,
    /* *** END OF REQUIRED FIELDS *** */
    description: zod_1.z
        .string()
        .max(2000, 'Description must be under 2000 characters')
        .transform((val) => (val === '' ? null : val))
        .nullish(),
    ownedBy: patent_schema_1.PatentOwnerSchema.nullish(),
    // Common dates
    priorityDate: zod_1.z.coerce
        .date()
        .refine((d) => d <= new Date(), {
        message: 'Priority date cannot be in the future',
    })
        .nullish(),
    filingDate: zod_1.z.coerce
        .date()
        .refine((d) => d <= new Date(), {
        message: 'Filing date cannot be in the future',
    })
        .nullish(),
    internationalPublicationDate: zod_1.z.coerce
        .date()
        .refine((d) => d <= new Date(), {
        message: 'International publication date cannot be in the future',
    })
        .nullish(),
    grantDate: zod_1.z.coerce
        .date()
        .refine((d) => d <= new Date(), {
        message: 'Grant date cannot be in the future',
    })
        .nullish(),
    expiryDate: zod_1.z.coerce
        .date()
        .refine((d) => d > new Date(), {
        message: 'Expiry date must be in the future',
    })
        .nullish(),
    renewalDueDate: zod_1.z.coerce
        .date()
        .refine((d) => d > new Date(), {
        message: 'Renewal due date must be in the future',
    })
        .nullish(),
    // Territory info
    territory: zod_1.z
        .string()
        .transform((val) => (val === '' ? null : val))
        .nullish(),
    territoryCode: zod_1.z
        .string()
        .min(2, 'Select a valid territory code')
        .max(5, 'Territory code must be 2–5 letters')
        .transform((val) => (val === '' ? null : val))
        .nullish(),
    // Application / Publication numbers
    applicationNumber: zod_1.z
        .string()
        .regex(/^[A-Za-z0-9\-\/]*$/, 'Application number can only contain letters, numbers, dashes, and slashes')
        .max(50, 'Application number too long')
        .transform((val) => (val === '' ? null : val))
        .nullish(),
    publicationNumber: zod_1.z
        .string()
        .regex(/^[A-Za-z0-9\-\/]*$/, 'Publication number can only contain letters, numbers, dashes, and slashes')
        .max(50, 'Publication number too long')
        .transform((val) => (val === '' ? null : val))
        .nullish(),
});
// Provisional Patent Schema extended to make specific fields required
exports.ProvisionalPatentFormSchema = exports.BasePatentFormSchema.extend({
    patentType: zod_1.z.literal(patent_enums_1.PatentType.PROVISIONAL),
    priorityDate: zod_1.z.coerce.date().refine((d) => d <= new Date(), {
        message: 'Priority date cannot be in the future',
    }),
    territory: zod_1.z.string(),
    territoryCode: zod_1.z
        .string()
        .min(2, 'Select a valid territory code')
        .max(5, 'Territory code must be 2–5 letters'),
});
// PCT Patent Schema extended to make specific fields required
exports.PCTPatentFormSchema = exports.BasePatentFormSchema.extend({
    patentType: zod_1.z.literal(patent_enums_1.PatentType.PCT),
    // priority date is first patent that was filed
    priorityDate: zod_1.z.coerce.date().refine((d) => d <= new Date(), {
        message: 'Priority date cannot be in the future',
    }),
    filingDate: zod_1.z.coerce.date().refine((d) => d <= new Date(), {
        message: 'Filing date cannot be in the future',
    }),
    applicationNumber: zod_1.z
        .string()
        .regex(/^[A-Za-z0-9\-\/]*$/, 'Application number can only contain letters, numbers, dashes, and slashes')
        .max(50, 'Application number too long'),
});
// Full Patent Schema extended to make specific fields required
exports.FullPatentFormSchema = exports.BasePatentFormSchema.extend({
    patentType: zod_1.z.literal(patent_enums_1.PatentType.FULL),
    // priority date is first patent that was filed
    priorityDate: zod_1.z.coerce.date().refine((d) => d <= new Date(), {
        message: 'Priority date cannot be in the future',
    }),
    filingDate: zod_1.z.coerce.date().refine((d) => d <= new Date(), {
        message: 'Filing date cannot be in the future',
    }),
    applicationNumber: zod_1.z
        .string()
        .regex(/^[A-Za-z0-9\-\/]*$/, 'Application number can only contain letters, numbers, dashes, and slashes')
        .min(3, 'Application number is too short')
        .max(50, 'Application number too long'),
    publicationNumber: zod_1.z
        .string()
        .regex(/^[A-Za-z0-9\-\/]*$/, 'Publication number can only contain letters, numbers, dashes, and slashes')
        .max(50, 'Publication number too long'),
    territory: zod_1.z.string(),
    territoryCode: zod_1.z
        .string()
        .min(2, 'Select a valid territory code')
        .max(5, 'Territory code must be 2–5 letters'),
});
// Discriminate PatentFormSchema that changes validation requirements based on patent type
exports.PatentFormSchema = zod_1.z.discriminatedUnion('patentType', [
    exports.ProvisionalPatentFormSchema,
    exports.FullPatentFormSchema,
    exports.PCTPatentFormSchema,
]);
// TODO: Upload Patent Certificate (link StorageObject or PatentCertificate -> StorageObject)
